import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, Phone, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { centersAPI } from '../../utils/api';

const CenterSelectionModal = ({ isOpen, onClose, onSelectCenter }) => {
  const [centers, setCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadCenters();
    }
  }, [isOpen]);

  const loadCenters = async () => {
    try {
      const response = await centersAPI.getAll();
      setCenters(response.data);
    } catch (error) {
      console.error('Failed to load centers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCenterSelect = (centerId) => {
    const center = centers.find(c => c.id === centerId);
    setSelectedCenter(center);
  };

  const handleContinue = () => {
    if (selectedCenter) {
      onSelectCenter(selectedCenter);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            data-testid="center-modal-backdrop"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              data-testid="center-selection-modal"
            >
              {/* Header */}
              <div className="bg-primary text-white p-6 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Select Service Center</h2>
                  <p className="text-sm text-white/80 mt-1">Choose your preferred location</p>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  data-testid="close-modal-btn"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="spinner" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select a Center
                      </label>
                      <Select onValueChange={handleCenterSelect}>
                        <SelectTrigger className="h-14 text-base">
                          <SelectValue placeholder="Choose a service center..." />
                        </SelectTrigger>
                        <SelectContent>
                          {centers.map((center) => (
                            <SelectItem key={center.id} value={center.id} className="py-3">
                              <div className="flex items-center">
                                <MapPin className="h-4 w-4 mr-2 text-accent" />
                                <span className="font-medium">{center.name}</span>
                                <span className="text-muted-foreground ml-2">- {center.city}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Selected Center Details */}
                    <AnimatePresence>
                      {selectedCenter && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="bg-secondary rounded-lg p-4 space-y-3"
                        >
                          <h3 className="font-semibold text-lg text-primary">{selectedCenter.name}</h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-start">
                              <MapPin className="h-4 w-4 mr-2 mt-0.5 text-accent" />
                              <div>
                                <p className="font-medium">Address</p>
                                <p className="text-muted-foreground">{selectedCenter.address}</p>
                                <p className="text-muted-foreground">{selectedCenter.city}</p>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-2 text-accent" />
                              <div>
                                <p className="font-medium inline mr-2">Contact:</p>
                                <p className="text-muted-foreground inline">{selectedCenter.contact}</p>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-2 text-accent" />
                              <div>
                                <p className="font-medium inline mr-2">Hours:</p>
                                <p className="text-muted-foreground inline">Mon-Fri: 9:00 AM - 5:00 PM</p>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 p-6 flex justify-end space-x-4">
                <Button
                  variant="outline"
                  onClick={onClose}
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleContinue}
                  disabled={!selectedCenter}
                  className="bg-accent hover:bg-accent/90"
                  data-testid="continue-to-calendar-btn"
                >
                  Continue to Calendar
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CenterSelectionModal;